import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { t, toast, Tooltip } from '@kanbios/ui';
import { api, ApiFailure, unwrap } from '../lib/api';
import { clockTime } from '../lib/format';
import { rescanHold } from '../lib/rescan';
import { PendingButton } from './PendingButton';
import type { Running } from './RunningPanel';

/**
 * D55 « Relancer l'analyse »: Trivy analyses the server's running images now,
 * instead of at the daily pass (an Administrator, once per server per 15
 * minutes). Shown when the API offers it (`canRescan`); disabled, with the
 * reason in its tooltip, within the 15 minutes or when the server cannot take
 * it. The images then read « Analyse en cours » until their new analysis.
 */
export function RescanButton({ server, running }: { server: string; running: Running | undefined }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  if (!running?.canRescan) return null;
  const hold = rescanHold(running);
  const reason = !hold ? null
    : hold.kind === 'cooldown' ? t('running.rescan.cooldown', { requested: clockTime(hold.requestedAt), available: clockTime(hold.availableAt) })
      : t(`running.rescan.blocked.${hold.by}`);
  const rescan = async () => {
    setPending(true);
    try {
      queryClient.setQueryData(['server-running', server], unwrap(await api.fleetState.POST('/servers/{server}/running/rescan', { params: { path: { server } } })));
      toast({ tone: 'pos', title: t('running.rescan.started') });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
      else toast({ tone: 'neg', title: t('running.rescan.failed') });
      void queryClient.invalidateQueries({ queryKey: ['server-running', server] });
    } finally {
      setPending(false);
    }
  };
  const button = (
    <PendingButton variant="secondary" size="sm" icon="refresh" pending={pending} disabled={Boolean(reason)} onClick={() => void rescan()}>
      {t('running.rescan.action')}
    </PendingButton>
  );
  // A disabled button takes no focus: its reason is reached through its wrapper.
  return reason ? <Tooltip content={reason}><span tabIndex={0}>{button}</span></Tooltip> : button;
}
